import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RegistrationData, RegistrationStatus, RegistrationError, RegistrationErrorCode, registrationErrors } from '../../model/registration'
import { PortalApiError } from '@afterpaytouch/portal-api'
import { registrationsService } from '@afterpaytouch/portal-api/consumer/registrations'
import { accountService } from '@afterpaytouch/portal-api/consumer/account'
import { creditLimitService } from '@afterpaytouch/portal-api/consumer/creditLimit'

export enum WelcomeWithInitialLimitStatus {
  None = 'none',
  Seen = 'seen',
}

export interface TerminalErrorExtra {
  isInitialLimit?: boolean
}

export interface RegistrationState {
  id?: string
  status: RegistrationStatus
  data?: RegistrationData
  error?: RegistrationError
  isManualAddress: boolean
  welcomeWithInitialLimitStatus: WelcomeWithInitialLimitStatus
  terminalErrorExtra?: TerminalErrorExtra
  terminalErrorAcknowledged: boolean
}

const {
  endpoints: { createRegistration, confirmRegistrationMobile },
} = registrationsService

const {
  endpoints: { finaliseAccountDetails },
} = accountService

const applyTerminalErrorExtra = (state: RegistrationState, errorData?: PortalApiError): void => {
  if (errorData?.errorInfo?.isInitialLimit !== undefined) {
    state.terminalErrorExtra = { isInitialLimit: errorData.errorInfo.isInitialLimit }
  }
}

export const registrationInitialState: RegistrationState = {
  status: RegistrationStatus.NotStarted,
  isManualAddress: false,
  welcomeWithInitialLimitStatus: WelcomeWithInitialLimitStatus.None,
  terminalErrorAcknowledged: false,
}

const slice = createSlice({
  name: 'registration',
  initialState: registrationInitialState,
  reducers: {
    updateRegistrationStatus(state: RegistrationState, action: PayloadAction<RegistrationStatus>): RegistrationState {
      return {
        ...state,
        status: action.payload,
      }
    },
    updateRegistrationData(state: RegistrationState, action: PayloadAction<RegistrationData>): RegistrationState {
      return {
        ...state,
        data: { ...state.data, ...action.payload },
      }
    },
    resetRegistrationState(state) {
      state.status = RegistrationStatus.NotStarted
      state.data = undefined
      state.id = undefined
      state.error = undefined
      state.welcomeWithInitialLimitStatus = WelcomeWithInitialLimitStatus.None
      state.terminalErrorAcknowledged = false
    },
    toggleAddressMode(state: RegistrationState, action: PayloadAction<boolean>): RegistrationState {
      return {
        ...state,
        isManualAddress: action.payload,
      }
    },
    setWelcomeWithInitialLimitSeen(state: RegistrationState): RegistrationState {
      return {
        ...state,
        welcomeWithInitialLimitStatus: WelcomeWithInitialLimitStatus.Seen,
      }
    },
    setTerminalErrorExtra(state: RegistrationState, action: PayloadAction<TerminalErrorExtra>): RegistrationState {
      return {
        ...state,
        terminalErrorExtra: action.payload,
      }
    },
    setTerminalErrorAcknowledged(state: RegistrationState): RegistrationState {
      return {
        ...state,
        terminalErrorAcknowledged: true,
      }
    },
  },
  extraReducers: (builder) => {
    const isActionType =
      (type: string) =>
      (action: AnyAction): action is AnyAction =>
        action.type === type

    builder
      .addMatcher(createRegistration.matchFulfilled, (state, action) => {
        state.status = RegistrationStatus.Created
        state.id = action.payload.registrationId
      })
      .addMatcher(createRegistration.matchRejected, (state, action) => {
        state.status = RegistrationStatus.Error

        if (action.meta?.rejectedWithValue) {
          const errorPayload = action.payload as unknown as PortalApiError
          const errorCode = errorPayload?.errorCode
          const errorInfo = errorPayload?.errorInfo

          state.error = {
            error: errorCode ? registrationErrors[errorCode] ?? RegistrationErrorCode.Default : RegistrationErrorCode.Default,
            errorCode,
            errorInfo,
          }
        } else {
          const errorMessage = action.error?.message
          state.error = {
            error: RegistrationErrorCode.Default,
            errorCode: undefined,
            errorInfo: errorMessage ? { message: errorMessage } : undefined,
          }
        }
      })
      .addMatcher(confirmRegistrationMobile.matchFulfilled, (state) => {
        state.status = RegistrationStatus.CodeVerified
      })
      .addMatcher(confirmRegistrationMobile.matchRejected, (state, action) => {
        state.status = RegistrationStatus.Error

        if (action.meta?.rejectedWithValue) {
          const errorPayload = action.payload as unknown as PortalApiError
          const errorCode = errorPayload?.errorCode
          const errorInfo = errorPayload?.errorInfo

          state.error = {
            error: errorCode ? registrationErrors[errorCode] ?? RegistrationErrorCode.Default : RegistrationErrorCode.Default,
            errorCode,
            errorInfo,
          }
        } else {
          const errorMessage = action.error?.message
          state.error = {
            error: RegistrationErrorCode.Default,
            errorCode: undefined,
            errorInfo: errorMessage ? { message: errorMessage } : undefined,
          }
        }
      })
      .addMatcher(isActionType('registration/finaliseAccount/rejected'), (state, action) => {
        state.status = RegistrationStatus.Error

        if (action.meta?.rejectedWithValue) {
          const errorPayload = action.payload as PortalApiError
          const errorCode = errorPayload?.errorCode
          const errorInfo = errorPayload?.errorInfo

          state.error = {
            error: errorCode ? registrationErrors[errorCode] ?? RegistrationErrorCode.Default : RegistrationErrorCode.Default,
            errorCode,
            errorInfo,
          }
        } else {
          const errorMessage = action.error?.message
          state.error = {
            error: RegistrationErrorCode.Default,
            errorCode: undefined,
            errorInfo: errorMessage ? { message: errorMessage } : undefined,
          }
        }
      })
      .addMatcher(finaliseAccountDetails.matchFulfilled, (state) => {
        state.status = RegistrationStatus.Completed
        state.error = undefined
      })
      .addMatcher(creditLimitService.endpoints.getCreditLimit.matchRejected, (state, action) => {
        applyTerminalErrorExtra(state, action.payload?.data as PortalApiError | undefined)
      })
      .addMatcher(isActionType('consumer/creditLimit/rejected'), (state, action: AnyAction) => {
        applyTerminalErrorExtra(state, action.payload as PortalApiError | undefined)
      })
  },
})

export const { updateRegistrationData, updateRegistrationStatus, toggleAddressMode } = slice.actions

export const resetRegistrationState = slice.actions.resetRegistrationState

export const setWelcomeWithInitialLimitSeen = slice.actions.setWelcomeWithInitialLimitSeen

export const setTerminalErrorExtra = slice.actions.setTerminalErrorExtra

export const setTerminalErrorAcknowledged = slice.actions.setTerminalErrorAcknowledged

const { reducer } = slice
export const registrationReducer = reducer
